import type { LibraryPieceRef } from '@/lib/api/generated/model';

/**
 * Leaving a clashing piece to the Library (spec 212; service spec 166).
 *
 * Share refuses a piece whose key the Library already holds with different
 * content. The person can leave such a piece to the Library instead: it is not
 * written, and installs get the Library's item under that key. The service
 * says which refused pieces may be left (`can_use_library`); these rules say
 * which row offers what, and which choices survive a change of ticks.
 */

export type PieceKind = LibraryPieceRef['kind'];

const KIND_ORDER: Record<PieceKind, number> = {
  workflow: 0,
  agent: 1,
  skill: 2,
};

/** One piece's identity in the dialog's sets: "workflow:task". */
export function pieceId(kind: PieceKind, key: string): string {
  return `${kind}:${key}`;
}

function refOf(id: string): LibraryPieceRef {
  const at = id.indexOf(':');
  return { kind: id.slice(0, at) as PieceKind, key: id.slice(at + 1) };
}

/** The request's `use_library`, sorted so one choice is one cache entry. */
export function libraryRefs(deferred: ReadonlySet<string>): LibraryPieceRef[] {
  return [...deferred]
    .map(refOf)
    .sort(
      (a, b) =>
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.key.localeCompare(b.key),
    );
}

/**
 * The action a row offers.
 *
 * Only a piece the service marks as a clash may be left to the Library: a
 * refused row now, or an agent or skill the first check refused and the dialog
 * unticked. A row that uses the Library's offers its own version back. The
 * root and broken pieces are never marked, so they offer nothing.
 */
export function rowAction(row: {
  outcome: string | null;
  canUseLibrary: boolean;
}): 'use_library' | 'send_ours' | null {
  if (row.outcome === 'uses_library') return 'send_ours';
  if (
    row.canUseLibrary &&
    (row.outcome === 'refused' || row.outcome === 'not_sent')
  ) {
    return 'use_library';
  }
  return null;
}

/**
 * The choices that still stand after a change of ticks: an agent or skill left
 * to the Library is part of the share only while ticked, so clearing its tick
 * drops the choice. Workflows always travel, so theirs stand.
 */
export function keepTicked(
  deferred: ReadonlySet<string>,
  agents: ReadonlySet<string>,
  skills: ReadonlySet<string>,
): Set<string> {
  return new Set(
    [...deferred].filter((id) => {
      const { kind, key } = refOf(id);
      if (kind === 'agent') return agents.has(key);
      if (kind === 'skill') return skills.has(key);
      return true;
    }),
  );
}
